import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Configurações globais de formatação
 * Estes valores são substituídos pelo carregamento das configurações do backend
 */
let configuredTimezone = 'Europe/Lisbon';
let configuredDateFormat = 'dd/MM/yyyy';  // 'dd/MM/yyyy' ou 'yyyy-MM-dd'
let configuredTimeFormat = '24h';          // '24h' ou '12h'

/**
 * Obter o timezone actualmente configurado
 * @returns {string} Timezone configurado
 */
export function getTimezone() {
  return configuredTimezone;
}

/**
 * Definir o timezone configurado (chamado pelas Configurações)
 * @param {string} timezone - O novo timezone a usar
 */
export function setTimezone(timezone) {
  if (timezone) {
    configuredTimezone = timezone;
  }
}

/**
 * Obter o formato de data configurado
 * @returns {string} Formato de data ('dd/MM/yyyy' ou 'yyyy-MM-dd')
 */
export function getDateFormat() {
  return configuredDateFormat;
}

/**
 * Definir o formato de data configurado
 * @param {string} format - O formato de data a usar
 */
export function setDateFormat(format) {
  if (format) {
    configuredDateFormat = format;
  }
}

/**
 * Obter o formato de hora configurado
 * @returns {string} Formato de hora ('24h' ou '12h')
 */
export function getTimeFormat() {
  return configuredTimeFormat;
}

/**
 * Definir o formato de hora configurado
 * @param {string} format - O formato de hora a usar
 */
export function setTimeFormat(format) {
  if (format) {
    configuredTimeFormat = format;
  }
}

/**
 * Definir todas as configurações de uma vez
 * @param {object} config - Objeto com timezone, formato_data e formato_hora
 */
export function setAllFormats(config) {
  if (config.timezone) configuredTimezone = config.timezone;
  if (config.formato_data) configuredDateFormat = config.formato_data;
  if (config.formato_hora) configuredTimeFormat = config.formato_hora;
}

/**
 * Constante para compatibilidade retroativa
 */
export const TIMEZONE = 'Europe/Lisbon'; // Valor por defeito, use getTimezone() para valor dinâmico

/**
 * Formatar data para o timezone configurado
 * @param {string|Date} dateInput - Data em formato ISO ou objeto Date
 * @param {object} options - Opções de formatação do Intl.DateTimeFormat
 * @returns {string} Data formatada no timezone configurado
 */
export function formatDateLisbon(dateInput, options = {}) {
  if (!dateInput) return '-';
  
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  
  if (isNaN(date.getTime())) return '-';
  
  const defaultOptions = {
    timeZone: getTimezone(),
    ...options
  };
  
  return date.toLocaleString('pt-PT', defaultOptions);
}

/**
 * Formatar apenas a hora para o timezone configurado
 * Respeita o formato de hora configurado (24h ou 12h AM/PM)
 * @param {string|Date} dateInput - Data em formato ISO ou objeto Date
 * @param {boolean} includeSeconds - Se deve incluir segundos
 * @returns {string} Hora formatada (ex: "14:30" ou "2:30 PM")
 */
export function formatTimeLisbon(dateInput, includeSeconds = false) {
  if (!dateInput) return '-';
  
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  
  if (isNaN(date.getTime())) return '-';
  
  const is12h = configuredTimeFormat === '12h';
  const locale = is12h ? 'en-US' : 'pt-PT';
  
  const options = {
    timeZone: getTimezone(),
    hour: '2-digit',
    minute: '2-digit',
    hour12: is12h,
    ...(includeSeconds && { second: '2-digit' })
  };
  
  return date.toLocaleString(locale, options);
}

/**
 * Formatar apenas a data para o timezone configurado
 * Respeita o formato de data configurado (DD/MM/AAAA ou AAAA-MM-DD)
 * @param {string|Date} dateInput - Data em formato ISO ou objeto Date
 * @returns {string} Data formatada (ex: "31/03/2026" ou "2026-03-31")
 */
export function formatOnlyDateLisbon(dateInput) {
  if (!dateInput) return '-';
  
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  
  if (isNaN(date.getTime())) return '-';
  
  // Usar locale 'sv-SE' para formato ISO (yyyy-MM-dd)
  const locale = configuredDateFormat === 'yyyy-MM-dd' ? 'sv-SE' : 'pt-PT';
  
  return date.toLocaleString(locale, {
    timeZone: getTimezone(),
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

/**
 * Formatar data e hora completa para o timezone configurado
 * Respeita os formatos de data e hora configurados
 * @param {string|Date} dateInput - Data em formato ISO ou objeto Date
 * @param {boolean} includeSeconds - Se deve incluir segundos
 * @returns {string} Data e hora formatadas (ex: "31/03/2026 14:30" ou "2026-03-31 2:30 PM")
 */
export function formatDateTimeLisbon(dateInput, includeSeconds = false) {
  if (!dateInput) return '-';
  
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  
  if (isNaN(date.getTime())) return '-';
  
  const datePart = formatOnlyDateLisbon(date);
  const timePart = formatTimeLisbon(date, includeSeconds);
  
  return `${datePart} ${timePart}`;
}

/**
 * Extract error message from API error response
 * Handles both string and Pydantic validation error array formats
 */
export function getErrorMessage(error, fallback = 'Erro desconhecido') {
  const detail = error?.response?.data?.detail;
  
  if (!detail) {
    return error?.message || fallback;
  }
  
  // If detail is a string, return it directly
  if (typeof detail === 'string') {
    return detail;
  }
  
  // If detail is an array (Pydantic validation errors), extract messages
  if (Array.isArray(detail)) {
    const messages = detail.map(err => {
      if (typeof err === 'string') return err;
      if (err.msg) return err.msg;
      return JSON.stringify(err);
    });
    return messages.join('; ');
  }
  
  // If detail is an object, try to get a message
  if (typeof detail === 'object') {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  
  return fallback;
}
